<template>
  <div>
    <bm-grid-no-data v-if="virtualGrid.length === 0" variant="card">
      <slot name="no-data" />
    </bm-grid-no-data>

    <template v-else>
      <template v-for="(node, nodeIndex) in cardNodes">
        <bm-grid-show-all-rows
          v-if="node.kind === 'show-all'"
          :key="`show-all-${node.depth}-${nodeIndex}`"
          :remaining-rows="node.remaining"
          variant="card"
          :colspan="columnCount"
          @show-all="emit('show-all')"
        />

        <div
          v-else-if="node.kind === 'group'"
          :key="`group-${node.row.rowKey}`"
          class="card group"
          :class="groupCardClasses(node.depth)"
        >
          <div class="d-flex pa-2 card-top align-center">
            <bm-grid-row-expander
              :is-expanded="node.row.isExpanded"
              @toggle="emit('toggle-group-expand', node.row)"
            />
            <bm-grid-row-selector
              v-if="allowSelect"
              :checked="node.row.isSelected && rowSelectable(node.row)"
              :disabled="!rowSelectable(node.row)"
              :is-header="false"
              @change="emit('toggle-select', node.row)"
            />
            <div class="card-title">
              {{ groupTitle(node.row) }}
            </div>
          </div>
          <div
            v-if="groupAggregateEntries(node.row).length > 0"
            class="d-flex flex-wrap card-fields"
          >
            <div
              v-for="entry in groupAggregateEntries(node.row)"
              :key="entry.columnKey"
              class="pa-2"
            >
              <div class="heading">{{ entry.column.headingText }}</div>
              <div>
                <bm-grid-aggregate-cell
                  :column="entry.column"
                  :summary="entry.summary"
                  :value="entry.value"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          :key="`content-${node.row.rowKey}`"
          class="card content"
          :class="contentCardClasses(node.row, node.depth, node.rowIndex)"
        >
          <div
            class="d-flex pa-2 card-top align-center"
            @click="onContentCardClick(node.row)"
          >
            <bm-grid-row-selector
              v-if="allowSelect"
              :checked="node.row.isSelected && rowSelectable(node.row)"
              :disabled="!rowSelectable(node.row)"
              :is-header="false"
              @change="emit('toggle-select', node.row)"
            />
            <div class="card-title">
              <bm-grid-content-cell
                v-if="titleColumn"
                :column="titleColumn"
                :item="node.row.item"
                :align="false"
              />
            </div>
            <div v-if="actionColumn" class="card-actions">
              <bm-grid-content-cell
                :column="actionColumn"
                :item="node.row.item"
                :align="false"
              />
            </div>
          </div>
          <div
            v-if="fieldColumnEntries.length > 0"
            class="d-flex flex-wrap card-fields"
          >
            <div
              v-for="entry in fieldColumnEntries"
              :key="entry.key"
              class="pa-2 flex text-left justify-start"
              :class="`xs${entry.twelvePointWidth}`"
            >
              <div class="heading">{{ entry.column.headingText }}</div>
              <bm-grid-content-cell
                :column="entry.column"
                :item="node.row.item"
                :align="false"
              />
            </div>
          </div>
          <div v-if="isContentExpandable(node.row)">
            <div v-if="node.row.isExpanded" class="expand animateIn">
              <slot name="expand" v-bind="node.row" />
            </div>
            <div class="card-expand-controls d-flex justify-center">
              <bm-btn-expand
                :value="node.row.isExpanded"
                @update:value="onContentExpand(node.row, $event)"
              />
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import { BrewManError } from "@/app/errors";
import type {
  BmGridColumnConfiguration,
  BmGridColumnConfigurations,
  BmGridSummaryConfiguration,
  ContentRow,
  Row,
  RowGroup,
} from "@/components/controls/grid/BmGridData";
import BmGridAggregateCell from "@/components/controls/grid/BmGridAggregateCell.vue";
import BmGridContentCell from "@/components/controls/grid/BmGridContentCell.vue";
import BmGridNoData from "@/components/controls/grid/BmGridNoData.vue";
import BmGridRowExpander from "@/components/controls/grid/BmGridRowExpander.vue";
import BmGridRowSelector from "@/components/controls/grid/BmGridRowSelector.vue";
import BmGridShowAllRows from "@/components/controls/grid/BmGridShowAllRows.vue";
import BmBtnExpand from "@/components/controls/input/BmBtnExpand.vue";

/**
 * Hand-rolled mobile card list for BmGrid.
 * Emits semantic events; parent owns virtual-grid mutation and refresh.
 */

type CardNode =
  | { kind: "show-all"; remaining: number; depth: number }
  | { kind: "group"; row: RowGroup; depth: number }
  | { kind: "content"; row: ContentRow; depth: number; rowIndex: number };

type GroupAggregateEntry = {
  columnKey: string;
  column: BmGridColumnConfiguration;
  summary: BmGridSummaryConfiguration;
  value: unknown;
};

type FieldColumnEntry = {
  key: string;
  column: BmGridColumnConfiguration;
  twelvePointWidth: number;
};

interface BmGridCardViewProps {
  virtualGrid: readonly Row[];
  visibleColumns: BmGridColumnConfigurations;
  groupedColumns: BmGridColumnConfigurations;
  allowSelect: boolean;
  allowExpand: boolean;
  cardColumns: number;
  visibleRowLimit?: number | null;
  rowExpandable: (item: unknown) => boolean;
  rowSelectable: (item: unknown) => boolean;
  rowClasses: (row: unknown) => string[];
  isRowClickable: boolean;
}

const props = withDefaults(defineProps<BmGridCardViewProps>(), {
  visibleRowLimit: null,
});

interface BmGridCardViewEvents {
  (event: "toggle-select", row: Row): void;
  (event: "toggle-expand", row: ContentRow, isExpanded: boolean): void;
  (event: "toggle-group-expand", group: RowGroup): void;
  (event: "show-all"): void;
  (event: "row-click", row: ContentRow): void;
}

const emit = defineEmits<BmGridCardViewEvents>();

const slots = useSlots();

const columnCount = computed(() => Object.keys(props.visibleColumns).length);

const hasExpandSlot = computed(() => !!slots.expand);

const actionColumnKey = computed(
  () =>
    Object.entries(props.visibleColumns).find(
      ([, column]) => column.rendererMetadata && column.rendererMetadata.actions
    )?.[0]
);

const titleColumnKey = computed(
  () => Object.keys(props.visibleColumns)[0] as string | undefined
);

const titleColumn = computed(() =>
  titleColumnKey.value ? props.visibleColumns[titleColumnKey.value] : undefined
);

const actionColumn = computed(() =>
  actionColumnKey.value
    ? props.visibleColumns[actionColumnKey.value]
    : undefined
);

const fieldColumnEntries = computed((): FieldColumnEntry[] =>
  Object.keys(props.visibleColumns)
    .filter(
      (key) => key !== actionColumnKey.value && key !== titleColumnKey.value
    )
    .map((key) => {
      const column = props.visibleColumns[key];
      return {
        key,
        column,
        twelvePointWidth: getTwelvePointColumnValueForColumn(key, column),
      };
    })
);

const cardNodes = computed((): CardNode[] => walkRows(props.virtualGrid, 0));

function walkRows(rows: readonly Row[], depth: number): CardNode[] {
  const nodes: CardNode[] = [];

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    if (props.visibleRowLimit != null && props.visibleRowLimit === rowIndex) {
      nodes.push({
        kind: "show-all",
        remaining: rows.length - rowIndex,
        depth,
      });
      break;
    }

    const row = rows[rowIndex];

    if (row.rowType === "group") {
      const group = row as RowGroup;
      nodes.push({ kind: "group", row: group, depth });
      if (group.isExpanded) {
        nodes.push(...walkRows(group.children, depth + 1));
      }
    }

    if (row.rowType === "content") {
      nodes.push({
        kind: "content",
        row: row as ContentRow,
        depth,
        rowIndex,
      });
    }
  }

  return nodes;
}

function groupTitle(row: RowGroup): string {
  return `${props.groupedColumns[row.columnKey].headingText}: ${
    row.columnValue
  }`;
}

function groupCardClasses(depth: number): Record<string, boolean> {
  return {
    [`group-index-${depth}`]: true,
    [`depth-${depth}`]: true,
  };
}

function contentCardClasses(
  row: ContentRow,
  depth: number,
  rowIndex: number
): Record<string, boolean> {
  const classes: Record<string, boolean> = {
    even: rowIndex % 2 === 0,
    [`depth-${depth}`]: true,
  };
  props.rowClasses(row).forEach((newClass) => {
    classes[newClass] = true;
  });
  return classes;
}

function groupAggregateEntries(row: RowGroup): GroupAggregateEntry[] {
  return Object.keys(props.visibleColumns)
    .map((columnKey) => {
      const aggregateData = row.aggregateValues[columnKey];
      if (!aggregateData || aggregateData.summary.columnKey == undefined) {
        return null;
      }
      return {
        columnKey,
        column: props.visibleColumns[aggregateData.summary.columnKey],
        summary: aggregateData.summary,
        value: aggregateData.value,
      };
    })
    .filter((entry): entry is GroupAggregateEntry => entry !== null);
}

function isContentExpandable(row: ContentRow): boolean {
  return props.allowExpand && !!hasExpandSlot.value && props.rowExpandable(row);
}

function onContentCardClick(row: ContentRow): void {
  if (props.isRowClickable) {
    emit("row-click", row);
  }
}

function onContentExpand(row: ContentRow, isExpanded: boolean): void {
  emit("toggle-expand", row, isExpanded);
}

/**
 * Calculates the correct 12 point grid width for a card item based on the grid and column configuration.
 */
function getTwelvePointColumnValueForColumn(
  columnKey: string,
  column: BmGridColumnConfiguration
): number {
  if (column.cardColumnSpan < 1 || column.cardColumnSpan > props.cardColumns) {
    throw new BrewManError(
      `Invalid value for cardColumnSpan '${column.cardColumnSpan}' for column '${columnKey}'`
    );
  }

  const twelvePointGridColumnWidth = Math.floor(
    (12 / props.cardColumns) * column.cardColumnSpan
  );

  if (twelvePointGridColumnWidth < 1 || twelvePointGridColumnWidth > 12) {
    throw new BrewManError(
      `Invalid calculated value for grid column value '${twelvePointGridColumnWidth}' for column '${columnKey}'`
    );
  }

  return twelvePointGridColumnWidth;
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

@mixin heading-text
  @include bm-body
  color: $bm-color-text01

@mixin contents-text
  @include bm-body2
  color: $bm-color-text01

// Moved from BmGrid — scoped parent styles no longer reach this child SFC.
.card-top
  height: 100%
  border-bottom: 1px solid $bm-color-borders

.card-top .card-title
  flex-grow: 1 !important
  overflow-y: hidden

.card
  @include contents-text
  border: 1px solid $bm-color-borders
  border-radius: 5px
  margin-bottom: 10px

.card .heading
  @include heading-text

.card.group .heading
  color: inherit

.card.content.even
  background-color: $bm-color-background01

.card.content:not(.even)
  background-color: $bm-color-background04

.card.group.group-index-0, .card.group.group-index-0 :deep(.v-icon)
  background-color: #73777A
  color: $bm-color-text04

.card.group.group-index-1, .card.group.group-index-1 :deep(.v-icon)
  background-color: #989898
  color: white

.card.depth-1
  margin-left: 10px

.card.depth-2
  margin-left: 20px

.expand
  margin: 10px auto
  text-align: initial
  background-color: transparent

.card-expand-controls
  border-top: 1px solid $bm-color-borders

:deep(.hidden-icon)
  display: none

:deep(.sub-grid)
  padding: 5px 15px

.card.grid-row-error
  background-color: $bm-color-incomplete-row !important

.card.grid-row-warn
  background-color: $bm-color-warn-row !important

.card.grid-row-success
  background-color: $bm-color-successful-row !important

@keyframes slideIn
  0%
    opacity: 0
    transform: translateY(-20px)

  100%
    opacity: 1
    transform: translateY(0)

:deep(.animateIn)
  overflow: hidden
  animation: slideIn .3s ease-out
  transition: height .3s ease-out
</style>
